<template>
  <div>
    <div v-if="$i18n.locale === 'pe'">
      <Message message="" icon="coming_soon_cl.svg" :as-card="true" />
    </div>
    <div v-if="$i18n.locale === 'br'">
      <Message message="" icon="coming_soon_br.svg" :as-card="true" />
    </div>
    <div v-if="$i18n.locale === 'cl'" id="new-accounting-content">
      <Spinner v-if="isLoading" :is-loading="isLoading" />
      <Message
        v-else-if="serverError !== null "
        :message="serverError.message"
        :icon="serverError.icon"
        :as-card="true"
        :button-content="serverError.buttonContent || ''"
        @click="loadAccountableData" />
      <Message
        v-else-if="successfulAccounting !== null "
        :message="successfulAccounting.message"
        :icon="successfulAccounting.icon"
        :as-card="true"
        :button-content="successfulAccounting.buttonContent || ''"
        @click="loadAccountableData" />
      <div v-else-if="!isLoading">
        <div id="accountance-summary" class="card-item mt-6 has-overflow">
          <div v-if="accountableRows.length === 0" class="message-info has-text-centered">
            <i class="fas fa-exclamation-circle is-pulled-left" />
            <div class="message-text"><span>No hay registros por contabilizar</span></div>
          </div>
          <div v-else>
            <div
              class="columns">
              <div class="column mt-2 is-two-fifths is-flex is-flex-direction-column">
                <span class="is-flex">
                  <span class="icon-item-pending is-flex is-align-items-center is-justify-content-center">
                    <i class="fas fa-ellipsis has-text-warning is-size-4" />
                  </span>
                  <span>
                    <h4 class="has-text-grey has-text-weight-medium is-size-6">Hay {{ elementsToAccount }} registro{{ elementsToAccount > 1 ? 's' : '' }} por contabilizar</h4>
                  </span>
                </span>
                <div v-for="item in differenceAccountableSummary" :id="`balance-${item.name}`" :key="item.name">
                  <div v-if="item.value !== 0" class="is-flex is-align-items-center mx-1 my-4 is-size-7">
                    <div class="p-4"><i class="fa-solid fa-triangle-exclamation has-text-danger" /> Se detectó un descuadre en la contabilidad. Puedes dar aviso al equipo producto en el correo.</div>
                    <div><a href="mailto:g_producto_creditu@creditu.com"><b-button type="is-text" size="is-small">Avisar</b-button></a></div>
                  </div>
                  <div v-else class="mx-1 my-4 is-size-7"><i class="fa-solid fa-circle-check has-text-primary" /> Sin diferencia</div>
                </div>
                <div v-if="accountableRows.length > 0">
                  <b-button
                    id="download-excel"
                    class="is-rounded download-button"
                    size="is-small"
                    type="mr-4"
                    icon-left="download"
                    @click="downloadExcelFile">
                    <span>Descargar Excel</span>
                  </b-button>
                </div>
              </div>
              <div class="column">
                <div v-for="item in leftAccountableSummary" :id="`balance-${item.name}`" :key="item.name">
                  <div v-if="$t(item.name.toLowerCase()) !== 'Diferencia'" class="card-item shadowless assets-box">
                    <div class="has-text-primary"><b>{{ $t(item.name.toLowerCase()) }}</b></div>
                    <div :id="`balance-${item.name}-value`">{{ formatCurrency(item.value) }}</div>
                    <div v-for="i in differenceAccountableSummary" :id="`balance-${i.name}`" :key="i.name" class="difference-box is-size-7">
                      <div v-if="i.value !== 0" class="is-flex is-align-items-center mx-1 my-4">
                        <div class="mr-1"><b><i class="fa-solid fa-triangle-exclamation has-text-danger" /> {{ $t(i.name.toLowerCase()) }}:</b></div>
                        <div :id="`balance-${i.name}-value`">{{ formatCurrency(i.value) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="column">
                <div v-for="item in rightAccountableSummary" :id="`balance-${item.name}`" :key="item.name">
                  <div class="card-item shadowless">
                    <div class="has-text-primary"><b>{{ $t(item.name.toLowerCase()) }}</b></div>
                    <div :id="`balance-${item.name}-value`">{{ formatCurrency(item.value) }}</div>
                  </div>
                </div>
              </div>
            </div>
            <b-button
              id="account-button"
              :disabled="!checkUserFeature('element.accounting.button.contabilizar') || isAccountButtonIsDisabled"
              class="is-pulled-right is-button-primary"
              icon-right="angle-right"
              @click="isConfirmToAccountVisible = true">
              <span>Contabilizar</span>
            </b-button>
            <ConfirmAccountModal
              v-model="isConfirmToAccountVisible"
              :number-of-accountable-entities="accountableEntities.length"
              :accounting-button-disabled="isAccountingButtonDisabled"
              @account="setEntitiesAsAccounted" />
          </div>
        </div>
        <div v-if="pendingEntities.length > 0" id="pending-accountables" class="card-item has-overflow">
          <p>Existen <strong>{{ pendingEntities.length }}</strong> registros que quedaron fuera del listado de movimientos a contabilizar, en espera a que se completen sus datos:</p>
          <br>
          <b-table
            paginated
            hoverable
            :per-page="perPage"
            :current-page="currentPage"
            :data="pendingEntities"
            @page-change="$emit('update:currentPage', $event)">
            <b-table-column
              v-for="(column, index) in pendingEntitiesHeaders"
              :key="index"
              v-slot="props"
              :label="column.title">
              <p
                :class="{
                  tag: column.field === 'reason',
                  'is-danger': column.field === 'reason',
                  'is-light': column.field === 'reason',
                  'pending-row': true
                }">
                {{ column.field === 'amount' ? formatCurrency(props.row[column.field]) : props.row[column.field] }}
              </p>
            </b-table-column>
          </b-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import zipcelx from 'zipcelx';

import Spinner from '@/ui/components/Spinner.vue';
import ConfirmAccountModal from '@/ui/components/internal/accounting/ConfirmAccountModal.vue';
import { setTransactionsAsAccounted } from '@/application/services/api-clients/transactions';
import { setConsignmentsAsAccounted } from '@/application/services/api-clients/consignments';
import { setBankEntriesAsAccounted } from '@/application/services/api-clients/bank-entries';
import {
  getTransactionsToBeAccounted,
  getConsignmentsToBeAccounted,
  getBankEntriesToBeAccounted,
  getWalletsEntriesToBeAccounted,
  setWalletsEntriesAsAccounted
} from '@/application/services/api-clients/accounting';
import { dateToFormatedString, now } from '@/infrastructure/date';
import userFeatures from '@/ui/mixins/user-features';
import Message from '@/ui/components/Message.vue';
import { formatNumberByCountry } from '@/infrastructure/format';
import '@/ui/assets/css/custom-table.css';

export default {
  name: 'Accounting',
  components: { Spinner, ConfirmAccountModal, Message },
  mixins: [userFeatures],
  data() {
    return {
      isLoading: true,
      accountableRows: [],
      accountableEntities: [],
      accountableSummary: [],
      pendingEntities: [],
      pendingEntitiesHeaders: [
        { title: 'Tipo', field: 'type' },
        { title: 'Monto', field: 'amount' },
        { title: 'Identificador', field: '_id' },
        { title: 'Motivo', field: 'reason' }
      ],
      perPage: 10,
      currentPage: 1,
      isConfirmToAccountVisible: false,
      isAccountingButtonDisabled: false,
      loadingResult: false,
      successfulAccounting: null,
      serverError: null,
      elementsToAccount: 0,
      wasDifferenceWarningShown: false
    };
  },
  computed: {
    differenceAccountableSummary() {
      const names = ['difference'];
      return this.accountableSummary.filter(({ name }) => names.includes(name.toLowerCase()));
    },
    leftAccountableSummary() {
      const names = ['assets'];
      return this.accountableSummary.filter(({ name }) => names.includes(name.toLowerCase()));
    },
    rightAccountableSummary() {
      const names = ['liabilities', 'equity'];
      return this.accountableSummary.filter(({ name }) => names.includes(name.toLowerCase()));
    },
    isAccountButtonIsDisabled() {
      return this.accountableSummary.reduce((acc, { name, value }) => {
        if (name === 'DIFFERENCE' && value !== 0) {
          return true;
        }
        return false;
      }, true);
    }
  },
  async mounted() {
    if (this.$i18n.locale === 'cl') {
      await this.loadAccountableData();
    }
  },
  methods: {
    formatCurrency(number) {
      const fractionDigits = {
        cl: 0,
        br: 2,
        pe: 2
      }[this.$i18n.locale];
      const formattedNumber = formatNumberByCountry({
        number,
        countryIn: this.$i18n.locale,
        usePrefix: true,
        fractionDigits,
        currencyType: 'secondary'
      });
      return formattedNumber;
    },
    formatDate: (date, format = 'DD/MM/YYYY') => {
      return dateToFormatedString(date, format);
    },
    async getDataToBeAccounted() {
      try {
        const baseEmptyResults = {
          ready: {
            entities: [],
            rows: [],
            accountableSummary: {
              assets: 0,
              liabilities: 0,
              equity: 0,
              difference: 0
            }
          },
          pending: {
            entities: []
          }
        };
        const transactionsToBeAccounted = await getTransactionsToBeAccounted();
        const bankEntriesToBeAccounted = await getBankEntriesToBeAccounted();
        const walletsEntriesToBeAccounted = await getWalletsEntriesToBeAccounted();
        const consignmentsToBeAccounted = await getConsignmentsToBeAccounted();
        const preliminaryResults = [
          transactionsToBeAccounted,
          consignmentsToBeAccounted,
          bankEntriesToBeAccounted,
          walletsEntriesToBeAccounted
        ];
        // Fill missing fields with those from baseEmptyResults to prevent errors
        const allAccountableResults = preliminaryResults.map(result => {
          return {
            ready: { ...baseEmptyResults.ready, ...result?.ready },
            pending: { ...baseEmptyResults.pending, ...result?.pending }
          };
        });
        this.accountableRows = allAccountableResults.reduce((acc, { ready: { rows } }) => acc.concat(rows), []);
        this.accountableEntities = allAccountableResults.reduce((acc, { ready: { entities } }) => acc.concat(entities), []);
        this.elementsToAccount = this.accountableRows.length;
        this.accountableSummary = allAccountableResults.reduce((acc, { ready: { accountableSummary } }) => {
          Object.entries(accountableSummary).forEach(([key, value]) => {
            const index = acc.findIndex(e => e.name === key);
            if (index === -1) {
              acc.push({ name: key, value });
            } else {
              acc[index].value += value;
            }
          });
          return acc;
        }, []);
        this.pendingEntities = [
          ...transactionsToBeAccounted.pending.entities.map(({ _id, amount }) => ({ _id, amount, type: 'Transacción', reason: 'Falta fecha contable' })),
          ...consignmentsToBeAccounted.pending.entities.map(({ _id, amount }) => ({ _id, amount, type: 'Remesa', reason: 'Falta fecha contable' })),
          ...bankEntriesToBeAccounted.pending.entities.map(({ _id, amount }) => ({ _id, amount, type: 'Movimiento Bancario', reason: 'Falta fecha contable' })),
          ...walletsEntriesToBeAccounted.pending.entities.map(({ _id, amount }) => ({ _id, amount, type: 'Movimiento Billetera', reason: 'Falta fecha contable' }))
        ];
      } catch (_error) {
        this.serverError = {
          message: 'Hubo un problema al conectarnos con el servidor',
          icon: 'data_maintenance.svg',
          buttonContent: ''
        };
      }
    },
    async loadAccountableData() {
      this.isLoading = true;
      this.serverError = null;
      this.successfulAccounting = null;
      await this.getDataToBeAccounted();
      if (!this.accountableEntities.length) {
        this.isLoading = false;
        return;
      }
      this.isLoading = false;
    },
    async setEntitiesAsAccounted() {
      this.isAccountingButtonDisabled = true;
      const transactionsIds = this.accountableEntities.filter(e => e.debtAmount !== undefined).map(t => t._id);
      const consignmentsIds = this.accountableEntities.filter(e => e.totalSecondaryCurrencyConsignedAmount !== undefined).map(t => t._id);
      const bankEntriesIds = this.accountableEntities.filter(e => e.bankTransactionNumber !== undefined).map(t => t._id);
      const walletsEntriesIds = this.accountableEntities.filter(e => e.walletId !== undefined).map(t => t._id);
      try {
        if (transactionsIds.length > 0) {
          await setTransactionsAsAccounted(transactionsIds);
        }
        if (consignmentsIds.length > 0) {
          await setConsignmentsAsAccounted(consignmentsIds);
        }
        if (walletsEntriesIds.length > 0) {
          await setWalletsEntriesAsAccounted(walletsEntriesIds);
        }
        if (bankEntriesIds.length > 0) {
          await setBankEntriesAsAccounted(bankEntriesIds);
        }
        this.successfulAccounting = {
          message: `Se contabiliz${this.elementsToAccount > 1 ? 'aron' : 'ó'} ${this.elementsToAccount} registro${this.elementsToAccount > 1 ? 's' : ''}`,
          icon: 'sin-cuotas-pendientes.svg',
          buttonContent: 'Ok'
        };
      } catch (_error) {
        this.serverError = {
          message: 'Hubo un problema al conectarnos con el servidor',
          icon: 'data_maintenance.svg',
          buttonContent: 'Ok'
        };
      }
      this.isConfirmToAccountVisible = false;
      this.isAccountingButtonDisabled = false;
    },
    getAccountableRows() {
      const ids = this.accountableEntities.map(e => e._id);
      let CabOpeLinea = 1;
      return this.accountableRows.reduce((acc, row) => {
        const include = ids.includes(row.entityId);
        if (include) {
          acc.push({ ...row, CabOpeLinea });
          CabOpeLinea += 1;
        }
        return acc;
      }, []);
    },
    downloadExcelFile() {
      const fin700table = this.getAccountableRows();
      const [firstRow] = fin700table;
      delete firstRow.entityId;
      const keys = Object.keys(firstRow);
      const config = {
        filename: `fin700 ${now('DD-MM-YY-hh-MM')}`,
        sheet: {
          data: [keys.map(value => ({ value, type: 'string' }))]
        }
      };
      fin700table.forEach(fin700row => {
        delete fin700row.entityId; // eslint-disable-line no-param-reassign
        const values = Object.values(fin700row);
        const objects = values.map(value => ({ value, type: typeof value }));
        config.sheet.data.push(objects);
      });
      zipcelx(config);
    }
  }
};
</script>

<style>
  #content .card-item {
    margin-top: 20px;
  }
  .box {
    border-radius: 10px;
  }
  .entity-detailed-data.show {
    margin-top: 10px;
    padding: 20px;
    max-height: 300px;
  }
  .entity-detailed-data {
    width: 100%;
    max-height: 0px;
    height: auto;
    overflow: hidden;
    margin: auto;
    background-color: #F9F9F9;
    border-radius: 20px;
    padding: 0 30px;
    margin-top: 0px;
    font-size: 14px;
  }
  .entity-details {
    display: grid;
    gap: 0.3rem 1rem;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-columns: repeat(3, minmax(150px, 1fr));
    grid-template-areas: "col1 col2 col3";
    text-align: center;
    font-size: 0.8rem;
  }
  .entity-details .item {
    display: flex;
    flex-flow: column wrap;
  }
  .entity-details:first-of-type {
    margin-bottom: 0.5rem;
  }
  .message-info {
    display: grid;
    grid-template-columns: 1fr 10fr;
    gap: 1rem;
    align-items: center;
    background: #F2FBFB;
    padding: 20px;
    margin: 0.5rem 0rem 2rem 0rem;
    border-radius: 20px;
    font-family: var(--secondary-font);
    color: var(--color-nickel);
  }
  .message-info i {
    color: var(--primary);
  }
  #accounting-content #content {
    padding-bottom: 40px;
  }
  .is-button-secondary.active {
    color: white;
    background: var(--primary);
  }
  #accounting-content {
    margin-top: 3rem;
  }
  .modal-card-body {
    position: relative;
    padding-bottom: 40px;
  }
  .modal-card-body .close-modal {
    position: absolute;
    right: 20px;
    top:20px;
    width: 40px;
  }
  .modal-title {
    font-size: 18px;
    margin-bottom: 20px;
    padding-top: 40px;
  }
  .modal-content {
    margin-bottom: 30px;
    font-size: 14px;
    width: 100%;
    max-width: 400px;
  }
  .modal-content p {
    margin-bottom: 15px;
  }
  .entities-transition-enter-active {
    transition: opacity 0.5s linear, transform 0.5s cubic-bezier(0.2, 0.5, 0.1, 1);
    transition-delay: calc( 0.1s * var(--i) );
  }
  .entities-transition-enter, .entities-transition-leave-to, .entities-transition-leave-active {
    opacity: 0;
  }
  .entities-transition-enter {
    transform: translateX(-1em);
  }
  .entities-transition-leave-to {
    transform: translateX(1em);
  }
  .movement-type.field.is-grouped {
    margin-bottom: 0;
  }
  .entity-main-data {
    display:flex;
    flex-wrap: nowrap;
    justify-content: space-around;
    align-items: baseline;
    font-size: 1rem;
  }
  .card-item {
    margin-top: 20px;
  }
  .extend-button a {
    text-decoration: none;
  }
  .rotate {
    transform: rotate(180deg);
  }
  .assets-box {
    height: 199px;
  }

  .difference-box {
    position: absolute;
    bottom: 0px;
  }

  @media (max-width: 768px) {
    #accountance-summary .column {
      padding: 0px;
    }
    .assets-box {
    height: auto;
  }
  .difference-box {
    position: relative;
  }
  }
  @media (max-width: 640px) {
    .entity-details {
      grid-template-columns: repeat(1, 1fr);
      grid-template-areas: "col1";
    }
    .entity-details .item {
      display: grid;
      grid-template-columns: repeat(2, minmax(150px, 1fr));
      text-align: start;
    }
    .entity-details .item span {
      display: grid;
      grid-template-columns: repeat(1, minmax(150px, 1fr));
      text-align: start;
    }
    .movement-type.field.is-grouped {
      display: block;
    }
    .entity-main-data {
      display: block;
      text-align: center;
    }
    .entity-main-data > div {
      margin-bottom: 10px;
      display: block;
    }
  }
</style>

<style scoped>
  .field-button {
    height: 100%;
  }
  :deep(input.input.balance-text) {
    background-color: white !important;
    color: var(--primary) !important;
    font-weight: bold;
    list-style-type: none;
    min-width: fit-content !important;
    text-align: right;
  }
  .has-overflow {
    overflow: hidden;
  }
  :deep(i.fas.fa-download) {
    color: var(--secondary);
  }
</style>

<i18n>
  {
    "cl": {
      "assets": "Activos",
      "liabilities": "Pasivos",
      "equity": "Patrimonio",
      "difference": "Diferencia"
    },
    "pe": {
      "assets": "Activos",
      "liabilities": "Pasivos",
      "equity": "Patrimonio",
      "difference": "Diferencia"
    }
  }
  </i18n>
